/*!

=========================================================
* Material Kit PRO React - v1.8.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
// import reportWebVitals from './reportWebVitals';
import 'fontsource-roboto/latin.css'
import { firebaseApp } from "init-firebase.js"

import "assets/scss/material-kit-pro-react.scss?v=1.8.0";
import "assets/css/hitract-global.css";

import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from 'reducers/createStore';
import LoadingScreen from "views/Loading/LoadingScreen.js";

// Sentry
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

import App from "app";

// For material-ui theme
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import muiTheme from 'assets/themes/muiTheme.js'

// For redux-toastr
import ReduxToastr from 'react-redux-toastr'
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css'
import { initStateWithPrevTab } from 'redux-state-sync'

// Cookies consent
import { ConsentProvider, ConsentBanner } from 'react-hook-consent';
// import 'react-hook-consent/dist/styles/style.css';
import 'assets/css/react-hook-consent.css';
import CustomText from "components/CustomText/CustomText.js";
 
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

const root = ReactDOM.createRoot(document.getElementById('root'));

// const onBeforeLift = () => ({});
const onBeforeLift = async () => {
  await initStateWithPrevTab(store);
};

// Init sentry
if(process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: "https://f77b197a245d444ab558f213cccf12f1@o305934.ingest.sentry.io/1763358",
    normalizeDepth: 5,
    tracesSampleRate: 0.25,
    integrations: [
      new BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
              React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
          ),
      }),
    ]
  });
}

root.render(
  <Provider store={store}>
    <PersistGate 
      loading={<LoadingScreen />} 
      onBeforeLift={onBeforeLift}
      persistor={persistor}
    >
      <ConsentProvider
        options={{
            services: [
                {
                    id: 'necessaryCookies',
                    name: 'Nödvändiga, ej valbara kakor',
                    description: 'Dessa kakor är nödvändiga för att webbplatsen ska fungera och kan inte stängas av i våra system. De är vanligtvis bara inställda som svar på åtgärder som du har utfört som utgör en begäran om tjänster, till exempel att ställa in dina integritetspreferenser, att logga in eller att fylla i formulär. Du kan ställa in din webbläsare så att den blockerar eller varnar dig om dessa kakor.',
                    scripts: [],
                    cookies: [{ pattern: 'cf_clearance' }, { pattern: 'route' }, { pattern: 'SESSION'}, { pattern: 'G_ENABLED_IDPS' }],
                    // localStorage: ['local-storage-key'],
                    // sessionStorage: ['session-storage-key'],
                    mandatory: true,
                },
            ],
            // customHash: 'my-custom-hash', // optional, e.g. when changing the options based on language
            theme: 'light'
        }}
      >
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={muiTheme}>
              <App />
              <ConsentBanner
                settings={{ 
                  hidden: false, 
                  label: 'Mer', 
                  modal: { 
                    title: 'Våra kakor (cookies)',
                    description: 'Här kan du läsa mer om vilka kakor vi använder och varför.', 
                    decline: { hidden: true, label: 'Avböj' },
                    approve: { label: 'Godkänn' },
                    approveAll: { hidden: true, label: 'Godkänn alla' },
                  } 
                }}
                decline={{ hidden: false, label: 'Avböj' }}
                approve={{ label: 'Godkänn' }}
                approveAll={{ hidden: true, label: 'Godkänn alla' }}
              >
                  <CustomText>
                      Hitract använder kakor (cookies) för att göra din upplevese bättre. Vi strävar efter att använda så lite kakor som möjligt. Läs vår <a href="https://hitract.se/privacy">Integritetspolicy</a> för att förstå hur vi tryggar din integritet.
                  </CustomText>
              </ConsentBanner>
          </ThemeProvider>
        </StyledEngineProvider>
      </ConsentProvider>
      <ReduxToastr
        timeOut={4000}
        newestOnTop={false}
        preventDuplicates
        position="bottom-right"
        getState={(state) => state.toastr} // This is the default
        transitionIn="bounceIn"
        transitionOut="bounceOut"
        progressBar
        closeOnToastrClick
      />
    </PersistGate>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// serviceWorkerRegistration.register({
//   onUpdate: registration => {
//     if (registration && registration.waiting) {
//       registration.waiting.postMessage({ type: 'SKIP_WAITING' });
//     }
//     window.location.reload();
//   }
// });
serviceWorkerRegistration.unregister()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();

// Initialize service worker for notifications
// if ("serviceWorker" in navigator) {
//   navigator.serviceWorker
//     .register("/firebase-messaging-sw.js")
//     .then(function(registration) {
//       console.log("Registration successful, scope is:", registration.scope);
//     })
//     .catch(function(err) {
//       console.log("Service worker registration failed, error:", err);
//     });
// }